import { useEffect, useState, useMemo, useCallback } from 'react';
import WebApp from '@twa-dev/sdk';
import {
  initializeTelegramWebApp,
  isInsideTelegramWebApp,
  requestFullscreen as telegramRequestFullscreen,
  exitFullscreen as telegramExitFullscreen,
} from '../utils/telegram';

interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// Extended WebApp type with newer API properties (Bot API 8.0+)
interface ExtendedWebApp {
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  isFullscreen?: boolean;
  onEvent: (eventType: string, callback: () => void) => void;
  offEvent: (eventType: string, callback: () => void) => void;
}

// Cast WebApp to extended type for accessing newer properties
const ExtWebApp = WebApp as typeof WebApp & ExtendedWebApp;

/**
 * Hook для работы с Telegram WebApp API
 * Использует официальный @twa-dev/sdk для лучшей совместимости
 */
export const useTelegramWebApp = () => {
  const [isReady, setIsReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [safeAreaInset, setSafeAreaInset] = useState<SafeAreaInset>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const [contentSafeAreaInset, setContentSafeAreaInset] = useState<SafeAreaInset>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  useEffect(() => {
    try {
      // Инициализация WebApp через официальный SDK
      // Примечание: telegram.ts уже инициализировал Login Widget совместимость,
      // поэтому window.Telegram.WebApp.initData будет заполнен из localStorage если нужно
      initializeTelegramWebApp();
      
      // isReady = true после завершения инициализации (независимо от наличия данных)
      // Это нужно чтобы App.tsx мог проверить авторизацию
      setIsReady(true);
      
      // Получаем начальные значения safeAreaInset (Bot API 8.0+)
      // CSS переменные --tg-safe-area-inset-* устанавливаются автоматически telegram-web-app.js на :root
      // Итоговые значения вычисляются через max() в variables.css → --safe-top/bottom/left/right
      if (ExtWebApp.safeAreaInset) {
        setSafeAreaInset({
          top: ExtWebApp.safeAreaInset.top || 0,
          bottom: ExtWebApp.safeAreaInset.bottom || 0,
          left: ExtWebApp.safeAreaInset.left || 0,
          right: ExtWebApp.safeAreaInset.right || 0,
        });
      }
      
      // Получаем начальные значения contentSafeAreaInset (Bot API 8.0+)
      if (ExtWebApp.contentSafeAreaInset) {
        setContentSafeAreaInset({
          top: ExtWebApp.contentSafeAreaInset.top || 0,
          bottom: ExtWebApp.contentSafeAreaInset.bottom || 0,
          left: ExtWebApp.contentSafeAreaInset.left || 0,
          right: ExtWebApp.contentSafeAreaInset.right || 0,
        });
      }
      
      // Подписываемся на изменения safeAreaInset
      const handleSafeAreaChanged = () => {
        if (ExtWebApp.safeAreaInset) {
          setSafeAreaInset({
            top: ExtWebApp.safeAreaInset.top || 0,
            bottom: ExtWebApp.safeAreaInset.bottom || 0,
            left: ExtWebApp.safeAreaInset.left || 0,
            right: ExtWebApp.safeAreaInset.right || 0,
          });
        }
      };
      
      // Подписываемся на изменения contentSafeAreaInset
      const handleContentSafeAreaChanged = () => {
        if (ExtWebApp.contentSafeAreaInset) {
          setContentSafeAreaInset({
            top: ExtWebApp.contentSafeAreaInset.top || 0,
            bottom: ExtWebApp.contentSafeAreaInset.bottom || 0,
            left: ExtWebApp.contentSafeAreaInset.left || 0,
            right: ExtWebApp.contentSafeAreaInset.right || 0,
          });
        }
      };
      
      // Отслеживание fullscreen состояния
      const handleFullscreenChanged = () => {
        const fs = ExtWebApp.isFullscreen || false;
        setIsFullscreen(fs);
      };
      
      // Начальное состояние fullscreen
      setIsFullscreen(ExtWebApp.isFullscreen || false);
      
      // Слушаем события изменения safe area (Bot API 8.0+)
      if (ExtWebApp.onEvent) {
        ExtWebApp.onEvent('safeAreaChanged', handleSafeAreaChanged);
        ExtWebApp.onEvent('contentSafeAreaChanged', handleContentSafeAreaChanged);
        ExtWebApp.onEvent('fullscreenChanged', handleFullscreenChanged);
      }
      
      return () => {
        // Отписываемся от событий
        if (ExtWebApp.offEvent) {
          ExtWebApp.offEvent('safeAreaChanged', handleSafeAreaChanged);
          ExtWebApp.offEvent('contentSafeAreaChanged', handleContentSafeAreaChanged);
          ExtWebApp.offEvent('fullscreenChanged', handleFullscreenChanged);
        }
      };
    } catch (error) {
      console.warn('Failed to initialize Telegram WebApp:', error);
      // Инициализация завершена (с ошибкой или без)
      setIsReady(true);
    }
  }, []);

  // Получаем user: приоритет WebApp > localStorage
  // Используем useMemo чтобы не создавать новый объект при каждом рендере
  const user = useMemo(() => {
    // Сначала пробуем WebApp (Mini App)
    const webAppUser = WebApp.initDataUnsafe?.user;
    if (webAppUser && webAppUser.id) {
      return webAppUser;
    }
    
    // Затем пробуем localStorage (Login Widget)
    try {
      const storedAuth = localStorage.getItem('telegram_auth_data');
      if (storedAuth) {
        const params = new URLSearchParams(storedAuth);
        const id = parseInt(params.get('id') || '0');
        if (id > 0) {
          return {
            id,
            first_name: params.get('first_name') || '',
            last_name: params.get('last_name') || undefined,
            username: params.get('username') || undefined,
            photo_url: params.get('photo_url') || undefined,
            language_code: 'ru',
            is_premium: false,
          };
        }
      }
    } catch (e) {
      // Игнорируем ошибки парсинга
    }

    // Затем пробуем Google OAuth (session-based auth)
    try {
      const googleData = localStorage.getItem('google_user_data');
      if (googleData && localStorage.getItem('session_id')) {
        const parsed = JSON.parse(googleData);
        if (parsed.id) {
          return {
            id: parsed.id,
            first_name: parsed.firstName || parsed.first_name || '',
            last_name: parsed.lastName || parsed.last_name || undefined,
            username: parsed.username || parsed.email?.split('@')[0] || undefined,
            photo_url: parsed.avatarUrl || parsed.photo_url || undefined,
            language_code: 'ru',
            is_premium: false,
          };
        }
      }
    } catch (e) {
      // Игнорируем ошибки парсинга
    }

    return undefined;
  }, [isReady]); // Пересчитываем только когда isReady меняется

  // Управление полноэкранным режимом — стабильные коллбэки
  const requestFullscreen = useCallback(() => {
    telegramRequestFullscreen();
  }, []);
  
  const exitFullscreen = useCallback(() => {
    telegramExitFullscreen();
  }, []);

  return {
    webApp: WebApp,
    isReady,
    user,
    colorScheme: WebApp.colorScheme,
    themeParams: WebApp.themeParams,
    safeAreaInset, // Полная безопасная зона устройства (Bot API 8.0+)
    contentSafeAreaInset, // Безопасные зоны для контента (Bot API 8.0+)
    
    // Полноэкранный режим (Bot API 7.7+)
    isFullscreen, // Текущее состояние fullscreen
    requestFullscreen, // Принудительно войти в fullscreen
    exitFullscreen, // Выйти из fullscreen
    
    // Методы для haptic feedback (с проверкой поддержки и версии)
    // HapticFeedback требует версию WebApp >= 6.1
    haptic: {
      impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
        try {
          // Проверяем версию и наличие API
          const version = parseFloat(WebApp.version || '0');
          if (version >= 6.1 && WebApp.HapticFeedback && typeof WebApp.HapticFeedback.impactOccurred === 'function') {
            WebApp.HapticFeedback.impactOccurred(style);
          }
        } catch {
          // Игнорируем ошибки haptic feedback в браузере
        }
      },
      notification: (type: 'error' | 'success' | 'warning') => {
        try {
          const version = parseFloat(WebApp.version || '0');
          if (version >= 6.1 && WebApp.HapticFeedback && typeof WebApp.HapticFeedback.notificationOccurred === 'function') {
            WebApp.HapticFeedback.notificationOccurred(type);
          }
        } catch {
          // Игнорируем ошибки haptic feedback в браузере
        }
      },
      selection: () => {
        try {
          const version = parseFloat(WebApp.version || '0');
          if (version >= 6.1 && WebApp.HapticFeedback && typeof WebApp.HapticFeedback.selectionChanged === 'function') {
            WebApp.HapticFeedback.selectionChanged();
          }
        } catch {
          // Игнорируем ошибки haptic feedback в браузере
        }
      },
    },
    
    // Методы для кнопок
    mainButton: {
      show: () => WebApp.MainButton?.show(),
      hide: () => WebApp.MainButton?.hide(),
      setText: (text: string) => WebApp.MainButton?.setText(text),
      onClick: (callback: () => void) => WebApp.MainButton?.onClick(callback),
    },
    
    backButton: {
      show: () => WebApp.BackButton?.show(),
      hide: () => WebApp.BackButton?.hide(),
      onClick: (callback: () => void) => WebApp.BackButton?.onClick(callback),
    },
    
    close: () => WebApp.close(),
    
    // Открытие ссылок во внешнем браузере (с fallback)
    openLink: (url: string) => {
      if (WebApp.openLink) {
        WebApp.openLink(url);
      } else {
        window.open(url, '_blank');
      }
    },
    
    // Проверка, находимся ли мы внутри Telegram WebApp
    isInsideTelegram: isInsideTelegramWebApp(),
  };
};

